// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@gcg/XDL/types";

import { screen } from "@gcg/XDL/Helpers";
import colors from "@gcg/XDL/PreStyle/colors";
import ModalPresentationPropsDefault from "@insite/mobius/Modal/presentationProps";
import { get } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import { css } from "styled-components";

export const modalSizeVariants = {
    small: 40,
    medium: 60,
    large: 95,
};

const textColor = get(colors, "text.main");
const closeIconColor = get(colors, "common.border");
const primaryMainColor = get(colors, "primary.main");
const backgroundColor = get(colors, "common.ui3");

export default {
    defaultProps: {
        ...ModalPresentationPropsDefault,
        closeButtonProps: {
            ...ModalPresentationPropsDefault?.closeButtonProps,
            css: css`
                position: absolute;
                top: 10px;
                right: 10px;
                padding: 0 8px;

                svg {
                    width: 20px;
                    height: 20px;
                }

                &,
                span {
                    color: ${closeIconColor};
                }

                &:hover {
                    background: transparent;
                    border-color: transparent;
                }
            `,
        },
        cssOverrides: {
            modalBody: css`
                position: relative;
                max-width: ${modalSizeVariants.small}%;
            `,
            modalTitle: css`
                border-top: 4px solid;
                border-top-color: ${primaryMainColor};
                padding: 30px 40px 20px 40px;
                ${screen("lte", "sm")`
                    padding: 0 20px 28px 20px;
                `}
                color: ${textColor};
                background-color: ${getColor("common.white")};

                h4 {
                    margin: 0;
                    margin-bottom: 0;
                    font-size: 28px;
                    line-height: 35px;
                }
            `,
            modalContent: css`
                padding: 0 40px 28px 40px;
                ${screen("lte", "sm")`
                    padding: 0 20px 28px 20px;
                    overflow: auto;
                `}
                color: ${textColor};
                background-color: ${getColor("common.white")};
                overflow-x: hidden;

                .modal-actions {
                    padding: 28px 20px;
                    background-color: ${backgroundColor};
                    display: flex;
                    margin: -28px -40px;
                    margin-top: 50px;
                    justify-content: flex-end;

                    ${screen("lte", "sm")`
                        padding: 28px 40px;
                        margin-top: 30px;
                    `}
                  
                    button {
                        margin-right: 20px;
                    }

                    button + button {
                        margin-right: 0;
                        margin-left: 0;
                    }

                    ${screen("lte", "xs")`
                        justify-content: center;
                        text-align: center;
                        flex-wrap: wrap;

                        button {
                            width: 100%;
                            margin-right: 0;
                            margin-bottom: 10px;
                            
                            &:last-child {
                                margin-bottom: 0;
                            }
                        }
                    `}
                }
            `,
            headlineTypography: css`
                @media only screen and (max-width: 426px) {
                    padding-top: 30px;
                }
            `,
        },
        closeButtonIconProps: {
            ...ModalPresentationPropsDefault?.closeButtonIconProps,
            color: "secondary",
        },
    },
} as xBaseTheme["modal"];
